import type { Kin, Mode } from '@trippath/shared';

import { iataOf } from '../../lib/airports';
import { wallOf } from '../../lib/clock';
import { plusDays } from '../../lib/countryFacts';

/** A day of another trip of the tree: the town its night is spent in, and how it got there. */
export interface KinDay {
  city: string;
  country: string;
  /** The way it came by this day, on the day it arrives. */
  by: Mode | null;
  /** Where that way came from. */
  from: string | null;
  /** When that way left, and the zone of where it left: its clock there. */
  at: string | null;
  zone: string | null;
  /** That way said as the trip's own are: a flight by its airports' codes, `ICN→HKG`. */
  codes: string | null;
  /** That way was cancelled. */
  gone: boolean;
}

/**
 * Each day of a branch by its date: where its people sleep, the day they arrive marked by the
 * way they came. The stop it sets out from is the trip it parts from: its days are that trip's.
 */
export function kinDays(kin: Kin): Map<string, KinDay> {
  const days = new Map<string, KinDay>();
  kin.stops.forEach((stop, at) => {
    if (at === 0 && stop.days === 0 && kin.partsAt?.city === stop.city) return;
    const before = kin.stops[at - 1];
    const by = before?.mode ?? null;
    const gone = before?.mode != null && before.cancelled.includes(before.mode);
    const flown = before?.mode === 'flight' ? before.ends : null;
    const from = iataOf(flown?.from);
    const to = iataOf(flown?.to);
    const codes = from && to ? `${from}→${to}` : null;
    const left = before?.departureAt ?? null;
    const zone = before?.zone ?? null;
    // A way overnight is said on the day it leaves, by its clock there.
    const leaves = left === null ? stop.start : wallOf(left, zone).day;
    const arrived = {
      city: stop.city,
      country: stop.country,
      by,
      codes,
      from: before?.city ?? null,
      at: left,
      zone,
      gone,
    };
    if (leaves < stop.start) days.set(leaves, arrived);
    // A stop of no nights still has its day: the one it is passed through.
    for (let day = stop.start; day < stop.end || day === stop.start; day = plusDays(day, 1)) {
      days.set(
        day,
        day === stop.start && leaves >= stop.start
          ? arrived
          : {
              city: stop.city,
              country: stop.country,
              by: null,
              codes: null,
              from: null,
              at: null,
              zone: null,
              gone: false,
            },
      );
    }
  });
  return days;
}

/** A day's branches in the order things happen: a way that leaves sooner first, then the rest. */
export function byTime<T extends { at: string | null }>(one: T, other: T): number {
  if (one.at && other.at) return one.at.localeCompare(other.at);
  if (one.at) return -1;
  return other.at ? 1 : 0;
}
